/**
 * The one conversion path out of the hymnal reference.
 *
 * Extracted from HymnPage 2026-09-04 for one reason: the aside had shipped
 * untracked, so after a month of the hymnal carrying 88% of fuguelab.com's
 * search clicks there was no way to tell whether a single reader had ever
 * followed it. "Where should the CTA go" was an argument with no data under it.
 *
 * `pageKind` is what makes the answer readable — search arrivals land on deep
 * hymn pages (shallow, seconds), while the index takes the engaged traffic
 * (4.13 pages/session in August). Those two audiences want different things and
 * the event has to be able to say which one clicked.
 *
 * Plain <a>, not <Link>: the hymnal router owns only /hymnal/*, so a react-router
 * Link to a marketing route would be captured here and 404 client-side.
 */

type PageKind = 'hymn' | 'index' | 'tune' | 'meter' | 'person' | 'section'

interface CanticleCtaProps {
  /** Which hymnal page kind this is rendered on — the analytics dimension. */
  pageKind: PageKind
  /** The specific page, e.g. a hymn number or a tune slug. Optional for indexes. */
  pageRef?: string
}

export function CanticleCta({ pageKind, pageRef }: CanticleCtaProps) {
  return (
    <aside className="mt-12 rounded-lg border border-border bg-card p-5">
      <p className="text-sm text-muted-foreground">
        Planning services? <strong className="text-foreground">Canticle</strong> keeps your hymn and anthem history, so
        you know what you sang last Advent.
      </p>
      <a
        href="https://fuguelab.com/music-manager"
        onClick={() => {
          // Same shape as @gjl/studio-chrome's engagement events — the hymnal
          // calls initGA4 in main.tsx, so window.gtag is defined by the time a
          // reader can click anything.
          window.gtag?.('event', 'hymnal_cta_click', {
            event_category: 'conversion',
            event_source: `hymnal-${pageKind}`,
            ...(pageRef && { page_ref: pageRef }),
          })
        }}
        className="mt-2 inline-block text-sm underline decoration-border underline-offset-4 hover:decoration-foreground"
      >
        See how it works
      </a>
    </aside>
  )
}
